import { Injectable } from '@angular/core';
import { Subscription, forkJoin, Observable } from 'rxjs';
import { BroadcastService, MsalService } from '@azure/msal-angular';
import { HttpClient } from '@angular/common/http';
import {
  User,
  SSOUSERI,
  IBasicAuthUserBackendTokenPayload,
  IBasicAuthUserTokenPayload,
  BasicAuthUserTokenPayload,
} from 'src/app/shared/constants/dfte-user-info';
import { MessagingService } from './messaging.service';
import { BUS_MESSAGE_KEY } from '../constants/message-bus';
import { environment } from '../../../environments/environment';
import { CookieService } from 'ngx-cookie-service';
import { Router } from '@angular/router';
import { jwtDecode } from 'jwt-decode';
import { MessageService } from 'src/app/features/dfte-grid-view/services/message.service';
import { loginService } from 'src/app/Login/login/login.service';

@Injectable({
  providedIn: 'root',
})
export class AuthenticationService {
  userData: User | any;
  basicAuthUserData: IBasicAuthUserTokenPayload | any;

  private fetchInProgress: boolean | any;
  private authListeners: Subscription[] = [];

  constructor(
    private authService: MsalService,
    private broadcastService: BroadcastService,
    private messagingService: MessagingService,
    private https: HttpClient,
    private cookieService: CookieService,
    private router: Router,
    private messageService: MessageService,
    private loginService: loginService
  ) {}

  /**
   *  trigger this method to initialize authentication flow
   *  when we trigger this from local, auth is not required, send a user to mimick the flow
   *  @param user
   */
  initializeAuth(user?: any): void {
    console.log('In initializeAuth');
    if (environment.isLocal) {
      this.triggerLocalAuth(user);
    } else {
      this.triggerSSOAuth();
    }
  }

  /**
   * @returns Logged in User and details,
   * better use this.messagingService.subscribe(BUS_MESSAGE_KEY.USER_DETAILS, () => {});
   */
  getUser() {
    console.log('In getUser');
    return this.userData;
  }

  /**
   * triggers the login flow
   */
  login() {
    this.authService.loginRedirect();
    console.log('Trying to log in now');
    // this.authService.loginRedirect([environment.AUTH_RESOURCE_LINK.webAPI, "openid", "profile", "user.read"]);
  }

  /**
   * logout user
   */
  logout() {
    this.authService.logout();
    this.messagingService.publish(BUS_MESSAGE_KEY.USER_DETAILS, null);
  }

  loginInProgress(): boolean {
    return this.fetchInProgress || this.authService.getLoginInProgress();
  }

  /**
   * unsubscribe all listeners
   */
  cleanListerners() {
    for (const subscriber of this.authListeners) {
      if (subscriber) {
        subscriber.unsubscribe();
      }
    }

    this.broadcastService.getMSALSubject().next(1);
  }

  /**
   *
   * @param user - the user with which to update the user details
   */
  private triggerLocalAuth(user: User): void {
    console.log('Trigerring Local Login');
    if (!user || !user.userId) {
      this.userData = new User(
        environment.LOCAL_AUTH.userId,
        environment.LOCAL_AUTH.username
      );
      this.messagingService.publish(
        BUS_MESSAGE_KEY.USER_DETAILS,
        this.userData
      );
      this.cookieService.set(
        BUS_MESSAGE_KEY.USER_DETAILS,
        JSON.stringify(this.userData)
      );
    } else {
      this.userData = new User(user.userId, user.userName);
      this.messagingService.publish(
        BUS_MESSAGE_KEY.USER_DETAILS,
        this.userData
      );
      this.cookieService.set(
        BUS_MESSAGE_KEY.USER_DETAILS,
        JSON.stringify(this.userData)
      );
    }
  }

  /**
   * checks if user present, else triggers the login flow
   */
  private triggerSSOAuth(): void {
    console.log('Trigerring SSO Login');
    const user = this.authService.getAccount();
    console.log(user);
    console.log('Printing ID_TOKEN NULL CHECK');
    if (user != null) console.log(user.idToken);
    if (user) {
      this.userData = new User(user?.userName, user.name);
      this.setupAuthListeners();
      this.messagingService.publish(
        BUS_MESSAGE_KEY.USER_DETAILS,
        this.userData
      );
      this.cookieService.set(
        BUS_MESSAGE_KEY.USER_DETAILS,
        JSON.stringify(this.userData)
      );
    } else {
      this.login();
    }
  }

  /**
   * setsup the auth listeners
   *  failure
   *  success
   *  acquireTokenFailure
   */
  private setupAuthListeners() {
    /* -- first clean the listeners if any -- */
    this.cleanListerners();
    this.listenLoginFailure();
    this.listenLoginSuccess();
    this.listenAcquireTokenFailure();
  }

  /**
   * check if the login failed
   * todo: Create a 401 and redirect
   */
  private listenLoginFailure() {
    console.log('Registering listenLoginFailure');
    /* -- 401-- */
    this.authListeners.push(
      this.broadcastService.subscribe('msal:loginFailure', () =>
        this.messagingService.publish(BUS_MESSAGE_KEY.USER_DETAILS, null)
      )
    );
  }

  /**
   * login is success
   * get user
   */
  private listenLoginSuccess() {
    console.log('Registering listenLoginSuccess');
    /*this.authListeners
    .push(
        this.broadcastService
        .subscribe("msal:loginSuccess", () => this.getUserProfile())
    );*/
    this.authListeners.push(
      this.broadcastService.subscribe('msal:loginSuccess', (payload) => {
        console.log('Printing Payload obtained after successfull login');
        console.log(payload);
        this.getUserProfile();
      })
    );
  }

  /**
   * if token failed, handle the failure
   */
  private listenAcquireTokenFailure() {
    console.log('Registering listenAcquireTokenFailure');
    //will work for acquireTokenSilent and acquireTokenPopup
    this.authListeners.push(
      this.broadcastService.subscribe('msal:acquireTokenFailure', (payload) =>
        this.handleTokenFailure(payload)
      )
    );
  }

  /**
   *
   * @param tokenFailureResponse - the token failure response
   */
  private handleTokenFailure(tokenFailureResponse: any) {
    console.log('Token failure custom message check');
    console.log(tokenFailureResponse);
    // console.log(this.authService.loginInProgress());
    if (
      tokenFailureResponse.errorDesc.indexOf('consent_required') !== -1 ||
      tokenFailureResponse.errorDesc.indexOf('interaction_required') !== -1
    ) {
      if (!this.authService.getLoginInProgress()) {
        //check for acquire silect token
        this.authService.acquireTokenPopup({ scopes: ['user.read'] }).then(
          (token) => {
            this.getUserProfile();
          },
          (error) => {}
        );
      }
    }
  }

  /**
   * fetch the user details from SSO
   * this may not be needed and hence not invoked
   */
  private getUserProfile() {
    console.log('In getUserProfile');
    return this.https
      .get<SSOUSERI>(environment.AUTH_RESOURCE_LINK.self)
      .subscribe(
        (data) => {
          this.userData.setssoUserDetails(data);
          return data;
        },
        (error) =>
          console.error(
            ' Http get request to MS Graph failed' + JSON.stringify(error)
          )
      );
  }

  // triggerBasicAuth(): void {
  //   // thisuserData = this.messagingService.getBus(BUS_MESSAGE_KEY.USER_DETAILS);
  //   console.log('Triggering Basic Auth Login');

  //   // Retrieve the token from localStorage
  //   const token = localStorage.getItem('authToken'); // Replace 'authToken' with your token key
  //   if (!token) {
  //     console.log('No token found. Redirecting to login.');
  //     this.router.navigate(['/login']);
  //     // this.BasicAuthlogin(); // Redirect to login if no token is found
  //     return;
  //   }

  //   try {
  //     // Decode the token to extract payload information
  //     const decodedToken: IBasicAuthUserBackendTokenPayload = jwtDecode(token); // Ensure jwt-decode library is imported
  //     console.log('Decoded Token:', decodedToken);

  //     // Validate the token expiration time
  //     const currentTime = Math.floor(Date.now() / 1000); // Get current time in seconds
  //     if (decodedToken.exp && decodedToken.exp > currentTime) {
  //       console.log('Token is valid. Proceeding with authentication.');

  //       let refinedToken: IBasicAuthUserTokenPayload =
  //         this.mapTokenToInterface(decodedToken);

  //       // Token is valid, set user data
  //       this.basicAuthUserData = new BasicAuthUserTokenPayload(refinedToken);
  //       // this.setupAuthListeners();

  //       // Publish user details to messaging service
  //       this.messagingService.publish(
  //         BUS_MESSAGE_KEY.USER_DETAILS,
  //         this.basicAuthUserData
  //       );

  //       // Optionally store user details in cookies for quick access
  //       this.cookieService.set(
  //         BUS_MESSAGE_KEY.USER_DETAILS,
  //         JSON.stringify(this.basicAuthUserData)
  //       );
  //       this.router.navigate(['/']);
  //     } else {
  //       console.log('Token has expired. Redirecting to login.');
  //       this.handleExpiredToken(token); // Handle token expiration
  //     }
  //   } catch (error) {
  //     console.error('Error decoding token:', error);
  //     this.router.navigate(['/login']); // Redirect to login if token is invalid
  //   }
  // }
  // mapTokenToInterface(token) {
  //   const mappedToken = { ...token };

  //   mappedToken.isAdmin = Boolean(mappedToken.isAdmin);
  //   mappedToken.isUserActive = Boolean(mappedToken.isUserActive);
  //   mappedToken.isFirstLogin = Boolean(mappedToken.isFirstLogin);
  //   // mappedToken.isLoggedIn = Boolean(mappedToken.isLoggedIn);

  //   mappedToken.email = mappedToken.username;
  //   mappedToken.userName = mappedToken.name;

  //   return mappedToken;
  // }

  // private handleExpiredToken(token: string): void {
  //   // If the flow supports for token refresh then will go for tokenAquire if it fails then we redirect to login.
  //   console.log('Handling expired token.');
  //   localStorage.removeItem('authToken'); // Clear the expired token
  //   this.messageService.showMessage('Your session is expired, please relogin');
  //   this.loginService.logout({ token, isExpired: true }).subscribe(() => {
  //     this.messageService.showMessage(
  //       'Your session is expired, please re-login',
  //       'error'
  //     );
  //     this.messagingService.publish(BUS_MESSAGE_KEY.USER_DETAILS, null); // Reset user details
  //     this.router.navigate(['/login']); // Redirect to login
  //   });
  // }
}
